import './ElevateDemo.scss';
import { useMemo, useState } from 'react';
import cn from 'classnames';
import { AgentSessionManager } from '@Components/AgentChat';
import { Button } from '@Components/Button';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { Page } from '@Components/Page';
import { Size } from '@Components/designConstants';
import { AgentOrderApprovalPanel } from '../AgentOrderApproval/AgentOrderApproval';
import { AgentCustomEvent, AgentResponse } from '../../utils/agent-client';
import { Agent, useAgentEndpoint } from '../../utils/agent-endpoints';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { AuditStreamPanel } from './AuditStreamPanel';
import { GovernanceAuditEvent, governanceAuditFromCustomEvent } from './audit-stream';
import { governancePolicyFromCustomEvent, LiveBuyingPolicy } from './governance-live';
import { DecisionTrailPanel } from './DecisionTrailPanel';
import { EffectivenessPanel } from './EffectivenessPanel';
import { DEMO_STEPS } from './elevate-demo-constants';
import { GovernancePanel } from './GovernancePanel';
import { NexxenDraftsPanel } from './NexxenDraftsPanel';
import { NewtonHandoffStep } from './NewtonHandoffStep';
import { NEWTON_HANDOFF } from './elevate-newton-data';
import { OrchestrationWebPanel } from './OrchestrationWebPanel';
import { PlanningRubricPanel } from './PlanningRubricPanel';
import { extractCapturedPlan } from './plan-telemetry';

const NEWTON_STEP = DEMO_STEPS.findIndex(s => s.key === 'newton-handoff');
const MEDIA_PLAN_STEP = DEMO_STEPS.findIndex(s => s.key === 'media-plan');

export const ElevateDemo = () => {
  const { cid: company } = useCompanyInfo();
  const [activeStep, setActiveStep] = useState(0);
  // The streaming budget approved in step 2, carried into step 3's planner as
  // grounding context. undefined until the channel plan is approved.
  const [approvedBudget, setApprovedBudget] = useState<number>();
  // The finalized planner run, captured live. Feeds both the decision trail and
  // the orchestration trace (which reads the real tool-call sequence off it), and
  // persists across steps so the Buy step's trace reflects the planning that ran.
  const [agentResponse, setAgentResponse] = useState<AgentResponse>();
  // Real backend governance events (governance.audit) streamed live during the planner run, so the
  // Buy step's audit trail renders the agent's actual AuditEvents rather than a client reconstruction.
  const [govAuditEvents, setGovAuditEvents] = useState<GovernanceAuditEvent[]>([]);
  // The resolved durable buying policy from the one-time governance.policy event,
  // captured live during the planner run so the Buy step's guardrail cards show the
  // constraints actually enforced this request (allow-list + spend ceilings).
  const [livePolicy, setLivePolicy] = useState<LiveBuyingPolicy>();
  // The agent transport hands us generic AG-UI custom events; the demo owns the
  // governance.* interpretation (governanceAuditFromCustomEvent / governancePolicy…),
  // keeping app concepts out of the shared agent API.
  const onCustomEvent = (event: AgentCustomEvent) => {
    const audit = governanceAuditFromCustomEvent(event);
    if (audit) {
      setGovAuditEvents(prev => [...prev, audit]);
    }
    const policy = governancePolicyFromCustomEvent(event);
    if (policy) {
      setLivePolicy(policy);
    }
  };

  const approvePlan = (streamingBudget: number) => {
    setApprovedBudget(streamingBudget);
    setActiveStep(MEDIA_PLAN_STEP);
  };

  return (
    <Page title='Elevate Demo' pageType='Elevate Demo'>
      <div className='elevate-demo'>
        <DemoStepper activeStep={activeStep} onSelect={setActiveStep} />
        <div className='elevate-demo-body'>
          <DemoStepContent
            activeStep={activeStep}
            company={company}
            approvedBudget={approvedBudget}
            onApprovePlan={approvePlan}
            agentResponse={agentResponse}
            onAgentResponse={setAgentResponse}
            govAuditEvents={govAuditEvents}
            livePolicy={livePolicy}
            onCustomEvent={onCustomEvent}
          />
        </div>
        <DemoFooter activeStep={activeStep} onSelect={setActiveStep} />
      </div>
    </Page>
  );
};

interface StepperProps {
  activeStep: number;
  onSelect: (index: number) => void;
}

const DemoStepper = ({ activeStep, onSelect }: StepperProps) => (
  <div className='elevate-demo-stepper'>
    {DEMO_STEPS.map((step, index) => (
      <InteractiveDiv
        key={step.key}
        className={cn('elevate-demo-step', { 'is-active': index === activeStep })}
        onClick={() => onSelect(index)}
      >
        {step.label}
      </InteractiveDiv>
    ))}
  </div>
);

interface StepContentProps {
  activeStep: number;
  company: string;
  approvedBudget: number | undefined;
  onApprovePlan: (streamingBudget: number) => void;
  agentResponse: AgentResponse | undefined;
  onAgentResponse: (response: AgentResponse) => void;
  govAuditEvents: GovernanceAuditEvent[];
  livePolicy: LiveBuyingPolicy | undefined;
  onCustomEvent: (event: AgentCustomEvent) => void;
}

const DemoStepContent = ({
  activeStep,
  company,
  approvedBudget,
  onApprovePlan,
  agentResponse,
  onAgentResponse,
  govAuditEvents,
  livePolicy,
  onCustomEvent
}: StepContentProps) => {
  const step = DEMO_STEPS[activeStep];
  // The Newton pre-flight (step 1) has run once the demo has advanced past it.
  const newtonReplayed = activeStep > NEWTON_STEP;
  const segments = agentResponse?.segments ?? [];
  if (step.key === 'newton-handoff') {
    return (
      <NewtonHandoffStep
        blurb={step.blurb}
        approvedBudget={approvedBudget}
        onApprove={onApprovePlan}
      />
    );
  }
  if (step.key === 'media-plan') {
    return (
      <MediaPlanStep
        company={company}
        approvedBudget={approvedBudget}
        newtonReplayed={newtonReplayed}
        agentResponse={agentResponse}
        onAgentResponse={onAgentResponse}
        onCustomEvent={onCustomEvent}
        livePolicy={livePolicy}
      />
    );
  }
  if (step.key === 'buy') {
    return (
      <BuyStep
        company={company}
        blurb={step.blurb}
        segments={segments}
        newtonReplayed={newtonReplayed}
        govAuditEvents={govAuditEvents}
        livePolicy={livePolicy}
      />
    );
  }
  return <PlaceholderStep label={step.label} blurb={step.blurb} />;
};

interface MediaPlanStepProps {
  company: string;
  approvedBudget: number | undefined;
  newtonReplayed: boolean;
  agentResponse: AgentResponse | undefined;
  onAgentResponse: (response: AgentResponse) => void;
  onCustomEvent: (event: AgentCustomEvent) => void;
  livePolicy: LiveBuyingPolicy | undefined;
}

// Grounding context handed to the planner so it starts from the approved budget
// instead of asking for it — sent once as a leading system message on the first
// prompt (AgentCore session memory carries it forward).
const handoffContext = (budget: number) =>
  `The Bliss Point Audience & Planner pre-flight for ELEVATE is complete and its outputs have been ` +
  `accepted. Selected ` +
  `hypothesis (${NEWTON_HANDOFF.hypothesisLabel}): ${NEWTON_HANDOFF.selectedHypothesis} Primary ` +
  `audience: ${NEWTON_HANDOFF.primaryAudience.name} ` +
  `(~${(NEWTON_HANDOFF.primaryAudience.size / 1_000_000).toFixed(1)}M). Behavior change — Get: ` +
  `${NEWTON_HANDOFF.commsStrategy.get}; To: ${NEWTON_HANDOFF.commsStrategy.to}; By: ` +
  `${NEWTON_HANDOFF.commsStrategy.by}. Chosen investment scenario: Balanced Awareness & Sales. ` +
  `Goals: ${NEWTON_HANDOFF.goals} Primary KPI: ${NEWTON_HANDOFF.primaryKpi}. ` +
  `Flight: ${NEWTON_HANDOFF.flight} (start ${NEWTON_HANDOFF.flightStart}, end ${NEWTON_HANDOFF.flightEnd}) ` +
  `— draft all line items to this window. Geography: United States only — plan the US streaming ` +
  `portion of the campaign; do not plan non-US markets. Channel history: ${NEWTON_HANDOFF.channelHistory} ` +
  `Planning assumptions: no 1P data (MRI/3P audience only); creative is :30 video only; no major inventory ` +
  `restrictions and no brand-safety concerns above normal levels. Prioritize these streaming networks, in ` +
  `this order where efficient: ` +
  `${NEWTON_HANDOFF.networkPriority.join(', ')}. ` +
  `The full streaming/video media budget for this flight is $${budget.toLocaleString('en-US')} ` +
  `($${NEWTON_HANDOFF.ctvBudget.toLocaleString('en-US')} CTV + ` +
  `$${NEWTON_HANDOFF.olvBudget.toLocaleString('en-US')} OLV). IMPORTANT — a reserved sponsorship is carved ` +
  `out of the CTV total and MUST NOT be planned by you: ` +
  `$${NEWTON_HANDOFF.worldCupHoldout.toLocaleString('en-US')} of the CTV budget is a Netflix Women's World ` +
  `Cup streaming sponsorship handled separately; do NOT plan or draft ANY line items against it. Plan CTV ` +
  `direct insertion-order lines against the remaining ` +
  `$${(NEWTON_HANDOFF.ctvBudget - NEWTON_HANDOFF.worldCupHoldout).toLocaleString('en-US')} only, and ` +
  `allocate that budget DIFFERENTIALLY across the prioritized streaming networks using your standard ` +
  `planning rubric (weight spend toward the higher-priority, more efficient networks) — do NOT split it ` +
  `evenly across networks. As a STARTING recommendation for that split (Bliss Point Audience & ` +
  `Planner's practical allocation ` +
  `within the streaming budget, applied to this plannable CTV amount — treat it as a starting point and ` +
  `adjust per your planning rubric for efficiency and feasibility): Netflix 35–40%; Hulu + Disney+ ` +
  `20–25% combined; Peacock + Paramount+ 10–15% combined; Tubi + Pluto TV 5–10% combined. Exclude ` +
  `YouTube and Twitch — they are not streaming line items you draft. Do NOT apply an audience segment ` +
  `to CTV lines. OLV / programmatic budget ` +
  `(Nexxen): ` +
  `$${NEWTON_HANDOFF.olvBudget.toLocaleString('en-US')} — this is an intentional budget: plan Nexxen ` +
  `OLV line(s) against it and let them flow through to programmatic placements; apply no minimum-entry ` +
  `or "entry-model" threshold beyond the $2,500/week per-line floor. Apply audiences to the OLV lines ` +
  `ONLY, one configured audience per line: ${NEWTON_HANDOFF.olvAudiences.join(' and ')} — named ` +
  `exactly as written. Flighting — build the plan to this weekly pulse (set flighting_strategy to it ` +
  `and pace spend to match) across the ${NEWTON_HANDOFF.flight} flight: ` +
  `${NEWTON_HANDOFF.flighting
    .map(
      p =>
        `${p.label} (${p.weeks}, ${p.dates}): CTV ~$${p.ctvSpend.toLocaleString('en-US')}, OLV ~$${p.olvSpend.toLocaleString('en-US')} — ${p.note}`
    )
    .join(' ')} ` +
  `The Women's World Cup burst is dominated by the held-out Netflix sponsorship, so tool-planned CTV is ` +
  `concentrated in the build and sustain phases; reflect this pacing in the plan. ` +
  `Full audience, category, and KPI detail are in your Bliss Point Audience & Planner ` +
  `pre-flight context pack.`;

// The planner step is real today: it embeds the same media-planning agent chat
// the Flight Plan page uses, so intake -> plan works end-to-end inside the demo.
const MediaPlanStep = ({
  company,
  approvedBudget,
  newtonReplayed,
  agentResponse,
  onAgentResponse,
  onCustomEvent,
  livePolicy
}: MediaPlanStepProps) => {
  const agentEndpoint = useAgentEndpoint(Agent.MEDIA_PLANNING);
  // The agent's own reasoning + segments, derived from the parent-persisted response
  // (not local state) so the decision trail and orchestration web survive a tab switch
  // — MediaPlanStep unmounts when the presenter navigates away and back.
  const plan = useMemo(
    () => (agentResponse ? extractCapturedPlan(agentResponse) : undefined),
    [agentResponse]
  );
  const segments = agentResponse?.segments ?? [];
  // Lets the presenter enlarge the chat to a tall, legible reading area so the
  // live agent reasoning is readable during the demo (default is a compact band).
  const [chatExpanded, setChatExpanded] = useState(false);

  return (
    <div className='elevate-demo-planner'>
      {approvedBudget !== undefined ? (
        <div className='elevate-demo-handoff'>
          Streaming budget from the accepted Bliss Point Audience &amp; Planner handoff:{' '}
          <strong>${approvedBudget.toLocaleString('en-US')}</strong> — CTV $
          {NEWTON_HANDOFF.ctvBudget.toLocaleString('en-US')} (direct-IO + programmatic; incl. $
          {NEWTON_HANDOFF.worldCupHoldout.toLocaleString('en-US')} reserved Netflix Women&apos;s
          World Cup sponsorship held out of planning) + OLV $
          {NEWTON_HANDOFF.olvBudget.toLocaleString('en-US')} (Nexxen programmatic), passed to the
          planner as grounding context.
        </div>
      ) : undefined}
      <div className='elevate-demo-chat-toolbar'>
        <Button size={Size.LG} onClick={() => setChatExpanded(v => !v)}>
          {chatExpanded ? 'Collapse chat' : 'Expand chat'}
        </Button>
      </div>
      <AgentSessionManager
        agentEndpoint={agentEndpoint}
        agent={Agent.MEDIA_PLANNING}
        company={company}
        sessionContext={approvedBudget !== undefined ? handoffContext(approvedBudget) : undefined}
        onCustomEvent={onCustomEvent}
        emptyState={
          <p className='elevate-demo-blurb'>
            Bliss Point Audience &amp; Planner handoff accepted — the planner already has
            ELEVATE&apos;s audience, objective, and budget as grounding. Pick the prompt below (or
            type your own) to build the streaming line-item plan.
          </p>
        }
        suggestedPrompts={[
          "Build ELEVATE's streaming line-item plan from the accepted Bliss Point Audience & Planner handoff (Balanced Awareness & Sales).",
          'Walk me through the plan you would build for ELEVATE and why, before drafting line items.'
        ]}
        onAgentResponse={response => onAgentResponse(response)}
        className={cn('elevate-demo-agent', { 'is-expanded': chatExpanded })}
      />
      <OrchestrationWebPanel
        company={company}
        segments={segments}
        newtonReplayed={newtonReplayed}
        livePolicy={livePolicy}
      />
      <PlanningRubricPanel />
      <EffectivenessPanel />
      {plan ? <DecisionTrailPanel plan={plan} /> : undefined}
    </div>
  );
};

interface BuyStepProps {
  company: string;
  blurb: string;
  segments: AgentResponse['segments'];
  newtonReplayed: boolean;
  govAuditEvents: GovernanceAuditEvent[];
  livePolicy: LiveBuyingPolicy | undefined;
}

// Embeds the real Agent Order Approvals governance panel so the draft -> approve
// -> (stop before Send) beat happens inside the demo. Send stays disabled until a
// draft is committed, and the server-side send guard blocks any un-committed send.
// The orchestration trace above it shows the governed sub-agents that produced the
// draft, so the "system of agents" story is visible right where the buy lands.
const BuyStep = ({
  company,
  blurb,
  segments,
  newtonReplayed,
  govAuditEvents,
  livePolicy
}: BuyStepProps) => (
  <div className='elevate-demo-buy'>
    <p className='elevate-demo-blurb'>{blurb}</p>
    <OrchestrationWebPanel
      company={company}
      segments={segments}
      newtonReplayed={newtonReplayed}
      livePolicy={livePolicy}
    />
    <AuditStreamPanel
      company={company}
      segments={segments}
      newtonReplayed={newtonReplayed}
      govAuditEvents={govAuditEvents}
    />
    <GovernancePanel company={company} livePolicy={livePolicy} />
    <AgentOrderApprovalPanel company={company} />
    <NexxenDraftsPanel company={company} />
  </div>
);

interface PlaceholderStepProps {
  label: string;
  blurb: string;
}

const PlaceholderStep = ({ label, blurb }: PlaceholderStepProps) => (
  <div className='elevate-demo-placeholder'>
    <h2 className='elevate-demo-placeholder-title'>{label}</h2>
    <p className='elevate-demo-blurb'>{blurb}</p>
    <p className='elevate-demo-soon'>Panel coming soon.</p>
  </div>
);

interface FooterProps {
  activeStep: number;
  onSelect: (index: number) => void;
}

const DemoFooter = ({ activeStep, onSelect }: FooterProps) => (
  <div className='elevate-demo-footer'>
    <Button size={Size.LG} disabled={activeStep === 0} onClick={() => onSelect(activeStep - 1)}>
      Back
    </Button>
    <Button
      size={Size.LG}
      disabled={activeStep === DEMO_STEPS.length - 1}
      onClick={() => onSelect(activeStep + 1)}
    >
      Next
    </Button>
  </div>
);
